import { useEffect, useState, useRef, useCallback } from "react";
import { api, fmtDate, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MapPin, Clock, ChevronLeft, LogOut, Truck, Play, Pause, CheckCircle2,
  PenLine, Camera, Package, ListChecks, User, Timer, Square, AlertTriangle, Trash2, Plus,
  Flag, AlarmClock, Paperclip, Download, StickyNote, ChevronDown, ChevronUp
} from "lucide-react";

function Signature({ onSave }) {
  const ref = useRef(null); const drawing = useRef(false);
  const start = (e) => { drawing.current = true; draw(e); };
  const end = () => { drawing.current = false; ref.current.getContext("2d").beginPath(); };
  const draw = (e) => {
    if (!drawing.current) return;
    const c = ref.current, r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    const ctx = c.getContext("2d"); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0F172A";
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  return (
    <div>
      <canvas ref={ref} width={300} height={120} data-testid="signature-pad"
        className="border border-slate-300 rounded-md bg-white w-full touch-none"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end} />
      <div className="flex gap-2 mt-2">
        <Button variant="outline" size="sm" onClick={() => ref.current.getContext("2d").clearRect(0, 0, 300, 120)}>Clear</Button>
        <Button size="sm" className="btn-accent" data-testid="signature-save" onClick={() => onSave(ref.current.toDataURL())}>Save signature</Button>
      </div>
    </div>
  );
}

const riskCls = (r) => r === "Critical" ? "bg-red-100 text-red-700" : r === "High" ? "bg-orange-100 text-orange-700" : r === "Medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";

function PhotoThumb({ jobId, file, onRemove, canRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/jobs/${jobId}/files/${file.id}/download`, { responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(new Blob([res.data], { type: file.content_type })); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [jobId, file.id, file.content_type]);
  return (
    <div className="relative" data-testid={`tech-photo-${file.id}`}>
      {src ? (
        <img src={src} alt={file.name} className="h-20 w-20 object-cover rounded-md border border-slate-200 cursor-pointer"
          onClick={() => window.open(src, "_blank")} />
      ) : (
        <div className="h-20 w-20 rounded-md border border-slate-200 bg-slate-50 grid place-items-center"><Camera className="h-4 w-4 text-slate-300" /></div>
      )}
      {canRemove && (
        <button data-testid={`tech-photo-remove-${file.id}`} onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 h-5 w-5 grid place-items-center rounded-full bg-red-600 text-white shadow">
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default function Technician() {
  const { user, logout } = useAuth();
  const { company } = useTenant();
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);

  const loadFiles = useCallback((jid) => {
    if (jid) api.get(`/jobs/${jid}/files`).then((r) => setFiles(r.data)).catch(() => setFiles([]));
    else setFiles([]);
  }, []);

  const load = useCallback(() => { api.get("/jobs").then((r) => setJobs(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFiles(active?.id); }, [active?.id, loadFiles]);

  const uploadPhotos = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/jobs/${active.id}/files`, fd);
      }
      toast.success(list.length > 1 ? `${list.length} photos added` : "Photo added");
      loadFiles(active.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Photo upload failed"); }
    finally { setUploading(false); }
  };

  const removePhoto = async (f) => {
    try { await api.delete(`/jobs/${active.id}/files/${f.id}`); toast.success("Photo removed"); loadFiles(active.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
  };

  const patch = async (extra, msg) => {
    const { data } = await api.put(`/jobs/${active.id}`, extra);
    setActive(data); load(); if (msg) toast.success(msg);
  };

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (active?.onsite_start && !active?.onsite_end) {
      const t = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(t);
    }
  }, [active?.onsite_start, active?.onsite_end]);

  const fmtDur = (mins) => { const h = Math.floor(mins / 60), m = mins % 60; return h > 0 ? `${h}h ${m}m` : `${m}m`; };
  const fmtLive = (ms) => { const s = Math.max(0, Math.floor(ms / 1000)); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60; return `${h}h ${String(m).padStart(2, "0")}m ${String(ss).padStart(2, "0")}s`; };
  const hhmm = (isoStr) => isoStr ? new Date(isoStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  const startOnSite = () => { const t = new Date().toISOString(); patch({ operational_status: "In Progress", onsite_start: t, arrival_time: t, onsite_end: null }, "On site — timer started"); };
  const finishOnSite = () => {
    const startTs = active.onsite_start ? new Date(active.onsite_start).getTime() : Date.now();
    const endIso = new Date().toISOString();
    const mins = Math.max(0, Math.round((Date.now() - startTs) / 60000));
    patch({ onsite_end: endIso, departure_time: endIso, time_on_site_minutes: mins, labour_hours: Math.round((mins / 60) * 100) / 100 }, `Finished — ${fmtDur(mins)} on site`);
  };

  const [defDraft, setDefDraft] = useState({ description: "", risk_rating: "Medium", recommended_action: "" });

  const [site, setSite] = useState(null);
  const [siteOpen, setSiteOpen] = useState(false);
  useEffect(() => {
    setSite(null); setSiteOpen(false);
    if (active?.site_id) api.get(`/sites/${active.site_id}`).then((r) => setSite(r.data)).catch(() => setSite(null));
  }, [active?.site_id]);
  const addDefect = () => {
    if (!defDraft.description.trim()) { toast.error("Describe the defect first"); return; }
    const list = [...(active.defects || []), { description: defDraft.description.trim(), risk_rating: defDraft.risk_rating, recommended_action: defDraft.recommended_action.trim() }];
    patch({ defects: list }, "Defect added");
    setDefDraft({ description: "", risk_rating: "Medium", recommended_action: "" });
  };
  const removeDefect = (idx) => patch({ defects: (active.defects || []).filter((_, i) => i !== idx) });

  const completeJob = async () => {
    const nowIso = new Date().toISOString();
    const extra = { operational_status: "Completed on Site", completion_date: nowIso, departure_time: nowIso, service_report_complete: true, billing_status: active.billing_status === "Not Ready" ? "Awaiting Manager Approval" : active.billing_status };
    if (active.onsite_start && !active.onsite_end) {
      const mins = Math.max(0, Math.round((Date.now() - new Date(active.onsite_start).getTime()) / 60000));
      extra.onsite_end = nowIso; extra.time_on_site_minutes = mins; extra.labour_hours = Math.round((mins / 60) * 100) / 100;
    }
    try {
      const { data } = await api.post(`/jobs/${active.id}/submit-report`, extra);
      setActive(data.job); load();
      toast.success(data.defects_created ? `Service report submitted · ${data.defects_created} defect(s) logged to register` : "Service report submitted");
    } catch (e) { toast.error("Submit failed"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const todays = jobs.filter((j) => (j.scheduled_date || "").slice(0, 10) === today);
  const upcoming = jobs.filter((j) => (j.scheduled_date || "").slice(0, 10) > today);

  if (active) {
    const a = active;
    return (
      <div className="min-h-screen bg-slate-50 pb-6">
        <header className="accent-bg text-white p-4 sticky top-0 z-10">
          <button onClick={() => setActive(null)} className="flex items-center gap-1 text-sm opacity-90"><ChevronLeft className="h-4 w-4" />Back to jobs</button>
          <h1 className="font-head font-bold text-lg mt-2">{a.ref} · {a.title}</h1>
          <div className="mt-1"><StatusBadge status={a.operational_status} /></div>
        </header>
        <div className="p-4 space-y-4">
          {a.sla_active && (
            <div data-testid="tech-sla-banner" className="rounded-md bg-red-600 text-white p-3">
              <div className="flex items-center gap-2 font-head font-bold text-sm"><AlarmClock className="h-4 w-4" />SLA JOB{a.sla_result ? ` — SLA ${a.sla_result.toUpperCase()}` : ""}</div>
              {a.sla_deadline && !a.sla_result && <p className="text-xs mt-1 opacity-90">Deadline: {new Date(a.sla_deadline).toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>}
              {a.sla_instructions && <p className="text-xs mt-1 opacity-90">{a.sla_instructions}</p>}
            </div>
          )}

          {(a.flagged_notes || []).length > 0 && (
            <div data-testid="tech-flagged-notes" className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 space-y-2">
              <h3 className="font-head font-bold text-sm text-amber-900 flex items-center gap-1.5"><Flag className="h-4 w-4 text-amber-600" />IMPORTANT — Flagged notes</h3>
              {(a.flagged_notes || []).map((n) => (
                <div key={n.id} data-testid={`tech-flagged-note-${n.id}`} className="text-sm text-amber-900 border-t border-amber-200 pt-2 first:border-0 first:pt-0">
                  <p className="whitespace-pre-wrap">{n.text}</p>
                  <p className="text-[10px] text-amber-700/70 mt-1">{n.updated_by ? `Updated by ${n.updated_by}` : `By ${n.created_by}`} · {new Date(n.updated_at || n.created_at).toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-md border border-slate-200 p-3 text-sm space-y-2">
            <div className="flex items-center gap-2 text-slate-700"><User className="h-4 w-4 text-slate-400" />{a.customer_name}</div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-slate-700 min-w-0"><MapPin className="h-4 w-4 text-slate-400 flex-shrink-0" /><span className="truncate">{a.site_name}</span></div>
              {site && (
                <button data-testid="tech-site-notes-btn" onClick={() => setSiteOpen((o) => !o)}
                  className="flex items-center gap-1 text-xs font-medium accent-text border border-slate-200 rounded-full px-2.5 py-1 active:bg-slate-50 flex-shrink-0">
                  <StickyNote className="h-3.5 w-3.5" />Site notes{siteOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
              )}
            </div>
            {siteOpen && site && (
              <div data-testid="tech-site-notes" className="rounded-md bg-slate-50 border border-slate-100 p-2.5 space-y-1.5 text-xs">
                {[["Address", site.address], ["Site contact", [site.site_contact, site.site_phone].filter(Boolean).join(" · ")],
                  ["Opening hours", site.opening_hours], ["Site notes / details", site.site_notes],
                  ["Access instructions", site.access_instructions], ["Site risks", site.site_risks]]
                  .filter(([, v]) => v).map(([l, v]) => (
                    <div key={l}><span className="text-slate-400 font-medium">{l}:</span> <span className="text-slate-700 whitespace-pre-wrap">{v}</span></div>
                  ))}
                {!site.site_notes && !site.access_instructions && !site.site_risks && !site.address && !site.opening_hours && !site.site_contact && (
                  <p className="text-slate-400">No site notes or details recorded.</p>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 text-slate-700"><Clock className="h-4 w-4 text-slate-400" />{fmtDate(a.scheduled_date)} {a.scheduled_time}</div>
            {a.customer_instructions && <p className="text-slate-500 border-t border-slate-100 pt-2">{a.customer_instructions}</p>}
            {a.description && <p className="text-slate-600">{a.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" data-testid="tech-acknowledge" onClick={() => patch({ operational_status: "Assigned", acknowledged: true }, "Acknowledged")}><ListChecks className="h-4 w-4 mr-1" />Acknowledge</Button>
            <Button variant="outline" data-testid="tech-enroute" onClick={() => patch({ operational_status: "Technician En Route" }, "En route")}><Truck className="h-4 w-4 mr-1" />En Route</Button>
            {a.onsite_start && !a.onsite_end ? (
              <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="tech-start" onClick={finishOnSite}><Square className="h-4 w-4 mr-1" />Finish</Button>
            ) : a.onsite_end ? (
              <Button variant="outline" data-testid="tech-start" onClick={startOnSite}><Play className="h-4 w-4 mr-1" />Restart timer</Button>
            ) : (
              <Button className="btn-accent" data-testid="tech-start" onClick={startOnSite}><Play className="h-4 w-4 mr-1" />Start</Button>
            )}
            <Button variant="outline" data-testid="tech-pause" onClick={() => patch({ operational_status: "Paused" }, "Paused")}><Pause className="h-4 w-4 mr-1" />Pause</Button>
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-3" data-testid="tech-time-on-site">
            <h3 className="font-head font-semibold text-sm flex items-center gap-1.5 mb-2"><Timer className="h-4 w-4 text-slate-400" />Time on site</h3>
            {!a.onsite_start ? (
              <>
                <p className="text-xs text-slate-500 mb-2">Tap Start when you arrive on site. We'll time the visit and calculate the total when you finish.</p>
                <Button className="btn-accent w-full" data-testid="tech-onsite-start" onClick={startOnSite}><Play className="h-4 w-4 mr-1" />Start on site</Button>
              </>
            ) : !a.onsite_end ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">On site since {hhmm(a.onsite_start)}</span>
                  <span className="flex items-center gap-1.5 text-emerald-600 font-medium"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />Live</span>
                </div>
                <div className="text-3xl font-head font-bold text-slate-900 my-2 tabular-nums" data-testid="tech-onsite-elapsed">{fmtLive(now - new Date(a.onsite_start).getTime())}</div>
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="tech-onsite-finish" onClick={finishOnSite}><Square className="h-4 w-4 mr-1" />Finish &amp; calculate</Button>
              </>
            ) : (
              <div className="text-sm space-y-1">
                <div className="flex items-center justify-between"><span className="text-slate-500">Started</span><span className="text-slate-800 font-medium">{hhmm(a.onsite_start)}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-500">Finished</span><span className="text-slate-800 font-medium">{hhmm(a.onsite_end)}</span></div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-1.5 mt-1"><span className="text-slate-600 font-medium">Total time on site</span><span className="text-lg font-head font-bold accent-text" data-testid="tech-onsite-total">{fmtDur(a.time_on_site_minutes || 0)}</span></div>
                <Button variant="outline" size="sm" className="w-full mt-2" data-testid="tech-onsite-restart" onClick={startOnSite}><Play className="h-4 w-4 mr-1" />Restart timer</Button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-3 space-y-3">
            <h3 className="font-head font-semibold text-sm">Record work</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Labour hours</Label>
                <div data-testid="tech-labour-hours" className="mt-1 h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  {a.labour_hours ? `${a.labour_hours} h` : a.time_on_site_minutes ? `${Math.round((a.time_on_site_minutes / 60) * 100) / 100} h` : "—"}
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">Auto from time on site</p>
              </div>
              <div><Label className="text-xs">Materials used</Label><Input data-testid="tech-materials" defaultValue={a.materials_used || ""} onBlur={(e) => patch({ materials_used: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Work notes</Label><Textarea data-testid="tech-notes" defaultValue={a.tech_notes || ""} onBlur={(e) => patch({ tech_notes: e.target.value })} className="mt-1" placeholder="Findings, tests, recommendations…" /></div>
            {(() => {
              const photos = files.filter((f) => f.is_photo || (f.content_type || "").startsWith("image/"));
              return (
                <div>
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2" data-testid="tech-photo-grid">
                      {photos.map((f) => (
                        <PhotoThumb key={f.id} jobId={a.id} file={f} canRemove={f.uploaded_by_id === user?.id}
                          onRemove={() => removePhoto(f)} />
                      ))}
                    </div>
                  )}
                  <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={uploadPhotos} />
                  <Button variant="outline" size="sm" className="w-full" data-testid="tech-photo" disabled={uploading}
                    onClick={() => photoRef.current?.click()}>
                    <Camera className="h-4 w-4 mr-1" />{uploading ? "Uploading…" : `Add photo (${photos.length})`}
                  </Button>
                  <p className="text-[10px] text-slate-400 mt-1">Photos are taken with your camera and attached to the service report.</p>
                </div>
              );
            })()}
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-3 space-y-3" data-testid="tech-defects">
            <h3 className="font-head font-semibold text-sm flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" />Defects found ({(a.defects || []).length})</h3>
            {(a.defects || []).length > 0 && (
              <div className="space-y-2">
                {(a.defects || []).map((d, i) => (
                  <div key={i} data-testid={`tech-defect-${i}`} className="flex items-start gap-2 bg-slate-50 border border-slate-100 rounded-md p-2">
                    <span className={`mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${riskCls(d.risk_rating)}`}>{d.risk_rating}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800">{d.description}</p>
                      {d.recommended_action && <p className="text-xs text-slate-500 mt-0.5">Action: {d.recommended_action}</p>}
                      {d.converted && <span className="text-[10px] text-emerald-600 flex items-center gap-1 mt-0.5"><CheckCircle2 className="h-3 w-3" />Logged as {d.defect_ref}</span>}
                    </div>
                    {!d.converted && <button data-testid={`tech-defect-remove-${i}`} onClick={() => removeDefect(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2 border-t border-slate-100 pt-2">
              <div><Label className="text-xs">Describe defect</Label><Textarea data-testid="tech-defect-desc" value={defDraft.description} onChange={(e) => setDefDraft((p) => ({ ...p, description: e.target.value }))} className="mt-1" placeholder="e.g. Zone 3 smoke detector faulty" /></div>
              <div>
                <Label className="text-xs">Risk rating</Label>
                <div className="flex gap-1.5 mt-1">
                  {["Low", "Medium", "High", "Critical"].map((r) => (
                    <button key={r} type="button" data-testid={`tech-defect-risk-${r}`} onClick={() => setDefDraft((p) => ({ ...p, risk_rating: r }))}
                      className={`flex-1 text-xs py-1.5 rounded-md border transition-colors ${defDraft.risk_rating === r ? riskCls(r) + " border-transparent font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs">Recommended action (optional)</Label><Input data-testid="tech-defect-action" value={defDraft.recommended_action} onChange={(e) => setDefDraft((p) => ({ ...p, recommended_action: e.target.value }))} className="mt-1" /></div>
              <Button variant="outline" size="sm" className="w-full" data-testid="tech-defect-add" onClick={addDefect}><Plus className="h-4 w-4 mr-1" />Add defect</Button>
            </div>
            <p className="text-[10px] text-slate-400">Defects are logged to the Defects register when you submit the service report.</p>
          </div>

          {files.filter((f) => !(f.is_photo || (f.content_type || "").startsWith("image/"))).length > 0 && (
            <div className="bg-white rounded-md border border-slate-200 p-3 space-y-2" data-testid="tech-files">
              <h3 className="font-head font-semibold text-sm flex items-center gap-1.5"><Paperclip className="h-4 w-4 text-slate-400" />Job files ({files.filter((f) => !(f.is_photo || (f.content_type || "").startsWith("image/"))).length})</h3>
              {files.filter((f) => !(f.is_photo || (f.content_type || "").startsWith("image/"))).map((f) => (
                <button key={f.id} data-testid={`tech-file-${f.id}`} onClick={async () => {
                  try {
                    const res = await api.get(`/jobs/${a.id}/files/${f.id}/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([res.data], { type: f.content_type }));
                    window.open(url, "_blank");
                  } catch { toast.error("Could not open file"); }
                }} className="w-full flex items-center gap-2 text-left text-sm text-slate-700 border border-slate-100 rounded-md p-2 active:bg-slate-50">
                  <Download className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-[10px] text-slate-400 uppercase">{f.ext}</span>
                </button>
              ))}
            </div>
          )}

          <div className="bg-white rounded-md border border-slate-200 p-3 space-y-2">
            <h3 className="font-head font-semibold text-sm">Customer sign-off</h3>
            <div><Label className="text-xs">Customer name</Label><Input data-testid="tech-cust-name" defaultValue={a.signoff_name || ""} onBlur={(e) => patch({ signoff_name: e.target.value })} className="mt-1" /></div>
            <Signature onSave={(dataUrl) => patch({ signature: dataUrl, service_report_complete: true }, "Signature captured")} />
            {a.signature && <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Signature captured</p>}
          </div>

          <Button className="btn-accent w-full py-6 text-base" data-testid="tech-complete" onClick={completeJob}>
            <CheckCircle2 className="h-5 w-5 mr-2" />Complete &amp; submit service report
          </Button>
        </div>
      </div>
    );
  }

  const Card = (j) => (
    <button key={j.id} data-testid={`tech-job-${j.id}`} onClick={() => setActive(j)} className="w-full text-left bg-white rounded-md border border-slate-200 p-3 active:bg-slate-50 transition-colors">
      <div className="flex items-start justify-between">
        <div><div className="font-head font-semibold text-slate-900">{j.title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{j.ref} · {j.customer_name}</div></div>
        <div className="flex items-center gap-1.5">
          {j.sla_active && <span data-testid={`tech-sla-tag-${j.id}`} className="text-[9px] font-bold text-white bg-red-600 rounded px-1 py-0.5">SLA</span>}
          {(j.flagged_notes || []).length > 0 && <Flag className="h-3.5 w-3.5 text-amber-500" />}
          <StatusBadge status={j.priority} />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.site_name}</span>
        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{j.scheduled_time || "—"}</span>
      </div>
      <div className="mt-2"><StatusBadge status={j.operational_status} /></div>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 max-w-md mx-auto pb-6">
      <header className="accent-bg text-white p-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          {company?.logo && (
            <img data-testid="tech-company-logo" src={`${API}/public/companies/${company.id}/logo?v=${company.logo_version || 0}`} alt={company?.name}
              className="h-9 max-w-[110px] object-contain bg-white rounded-md p-1 flex-shrink-0" />
          )}
          <div className="min-w-0"><div className="text-xs opacity-80 truncate">{company?.name}</div><h1 className="font-head font-bold text-lg">Hi, {user?.name?.split(" ")[0]}</h1></div>
        </div>
        <button data-testid="tech-logout" onClick={() => logout()} className="opacity-90"><LogOut className="h-5 w-5" /></button>
      </header>
      <div className="p-4 space-y-4">
        <div>
          <h2 className="font-head font-semibold text-slate-700 mb-2">Today ({todays.length})</h2>
          <div className="space-y-2">{todays.map(Card)}{todays.length === 0 && <p className="text-sm text-slate-400">No jobs scheduled today.</p>}</div>
        </div>
        <div>
          <h2 className="font-head font-semibold text-slate-700 mb-2">Upcoming ({upcoming.length})</h2>
          <div className="space-y-2">{upcoming.map(Card)}{upcoming.length === 0 && <p className="text-sm text-slate-400">Nothing upcoming.</p>}</div>
        </div>
        <p className="text-center text-xs text-slate-400 pt-2">Powered by Amden Dispatch</p>
      </div>
    </div>
  );
}
